import React, { useEffect, useRef, useState } from "react";
import { SectionCard, SourceLink } from "./common";
import { GoldButton, EmptyState, ui } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { UploadCloud, FileText, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = "image/png,image/jpeg,image/webp,.heic,.heif,application/pdf,.csv,.xlsx,.docx";

export function DocumentsTab({ property, options, registerUploader }) {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [cat, setCat] = useState("Other");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const cats = options.doc_categories || [];

  const load = () => api.get(`/properties/${property.id}/documents`).then((r) => setDocs(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [property.id]);

  const doUpload = async (file, category) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post(`/properties/${property.id}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document filed"); load();
    } catch { toast.error("Could not upload"); } finally { setBusy(false); }
  };

  // expose uploader to parent (Mortgage/Insurance buttons)
  useEffect(() => {
    if (registerUploader) registerUploader((category) => { setCat(category || "Other"); fileRef.current?.click(); });
  }); // re-register each render so the latest closure is used

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f, cat);
    if (fileRef.current) fileRef.current.value = "";
  };

  const visible = docs.filter((d) =>
    (filter === "All" || d.property_category === filter) &&
    (!q || (d.original_filename || "").toLowerCase().includes(q.toLowerCase()) || (d.title || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <SectionCard title="Documents" action={
      <div className="flex items-center gap-2">
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="doc-upload-category" className={`${ui.selectTrigger} mt-0 h-10 w-40`}><SelectValue /></SelectTrigger>
          <SelectContent className={ui.selectContent}>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <GoldButton data-testid="doc-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} Upload
        </GoldButton>
      </div>
    }>
      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onFile} data-testid="doc-file-input" />
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A99F90]" />
          <Input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents" className={`${ui.input} mt-0 pl-9`} />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="doc-filter" className={`${ui.selectTrigger} mt-0 w-44`}><SelectValue /></SelectTrigger>
          <SelectContent className={ui.selectContent}>
            <SelectItem value="All">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={FileText} title="No documents yet" hint="Upload purchase, mortgage, insurance, rates, repairs and tax documents — each kept with its full audit history." />
      ) : (
        <div className="space-y-2" data-testid="doc-list">
          {visible.map((d) => (
            <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center justify-between gap-3 bg-white/60 border border-[#EAE0D2] rounded-xl px-4 py-3">
              <div className="min-w-0">
                <div className="text-sm text-[#2C2A28] truncate">{d.original_filename}</div>
                <div className="text-xs text-[#A99F90]">{(d.created_at || "").slice(0, 10)} · {d.upload_method === "email" ? "Emailed" : "Uploaded"}{d.source_email ? ` · ${d.source_email}` : ""}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Badge className="bg-[#C6A45C]/15 text-[#8a6d2f] border-[#C6A45C]/30">{d.property_category || "Other"}</Badge>
                <SourceLink docId={d.id} testid={`doc-source-${d.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
